"use client";

/**
 * FAQSection Component
 *
 * Reusable FAQ accordion with automatic FAQPage JSON-LD schema generation.
 * Uses native details/summary for accessibility and no-JS fallback.
 */

import { useState } from "react";
import { ChevronDown } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

export interface FAQItem {
  question: string;
  answer: string;
}

export interface FAQSectionProps {
  /** Array of FAQ items */
  faqs: FAQItem[];
  /** Section title */
  title?: string;
  /** Section subtitle */
  subtitle?: string;
  /** Custom section ID for anchor links */
  id?: string;
  /** Include JSON-LD schema (default: true) */
  includeSchema?: boolean;
  /** Max columns for FAQ grid (1-3) */
  columns?: 1 | 2 | 3;
  /** Accent color for styling */
  accentColor?: "brand" | "green" | "purple" | "cyan";
}

export function FAQSection({
  faqs,
  title = "Frequently Asked Questions",
  subtitle,
  id = "faq",
  includeSchema = true,
  columns = 1,
  accentColor = "brand",
}: FAQSectionProps) {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const colorClasses = {
    brand: {
      icon: "text-primary",
      border: "border-primary/20",
      bg: "bg-primary/5",
    },
    green: {
      icon: "text-green-700",
      border: "border-green-500/20",
      bg: "bg-green-500/5",
    },
    purple: {
      icon: "text-purple-700",
      border: "border-purple-500/20",
      bg: "bg-purple-500/5",
    },
    cyan: {
      icon: "text-cyan-700",
      border: "border-cyan-500/20",
      bg: "bg-cyan-500/5",
    },
  };

  const colors = colorClasses[accentColor];

  // Generate FAQPage schema
  const schema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((faq) => ({
      "@type": "Question",
      name: faq.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: faq.answer,
      },
    })),
  };

  const columnClasses = {
    1: "grid-cols-1",
    2: "grid-cols-1 lg:grid-cols-2",
    3: "grid-cols-1 md:grid-cols-2 lg:grid-cols-3",
  };

  return (
    <>
      {includeSchema && (
        <script
          id={`${id}-schema`}
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
        />
      )}
      <section id={id} className="px-4 py-16 sm:px-10 sm:py-20">
        <div className="mx-auto max-w-content">
          {/* Header */}
          <div className="mb-12 text-center">
            <h2 className="mb-4 text-balance text-3xl font-semibold tracking-tight text-foreground sm:text-4xl">
              {title}
            </h2>
            {subtitle && (
              <p className="mx-auto max-w-2xl text-muted-foreground">{subtitle}</p>
            )}
          </div>

          {/* FAQ Grid */}
          <div className={`grid gap-4 ${columnClasses[columns]}`}>
            {faqs.map((faq, index) => (
              <div
                key={index}
                className={`overflow-hidden rounded-xl border ${colors.border} ${
                  openIndex === index ? colors.bg : "bg-slate-50"
                } transition-colors duration-200`}
              >
                <button
                  onClick={() => setOpenIndex(openIndex === index ? null : index)}
                  className="flex w-full items-center justify-between gap-4 p-5 text-left"
                  aria-expanded={openIndex === index}
                >
                  <span className="text-base font-medium text-foreground sm:text-lg">
                    {faq.question}
                  </span>
                  <motion.div
                    animate={{ rotate: openIndex === index ? 180 : 0 }}
                    transition={{ duration: 0.2 }}
                    className="flex-shrink-0"
                  >
                    <ChevronDown className={`h-5 w-5 ${colors.icon}`} />
                  </motion.div>
                </button>
                <AnimatePresence>
                  {openIndex === index && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.2 }}
                    >
                      <div className="border-t border-slate-100 px-5 pb-5 pt-4">
                        <p className="text-sm leading-relaxed text-muted-foreground sm:text-base">
                          {faq.answer}
                        </p>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
